import type { TFunction } from 'i18next';
import { Import, PlusIcon, SearchIcon } from 'lucide-react';

import React, { useMemo, useState } from 'react';

import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import { Alert, Button, Input } from '@splitfiesta/ui';

import { withAuth } from '@/components/auth';
import { PreloadLink } from '@/components/common/PreloadLink';
import { GlobalBalanceCard } from '@/components/dashboard/GlobalBalanceCard';
import { GroupsTable } from '@/components/groups/GroupsTable';
import { NativeAppCard } from '@/components/groups/NativeAppCard';
import { AppLayout } from '@/components/layout/AppLayout';
import { GroupsPageSkeleton } from '@/components/skeleton';
import { ActionButtonGroup } from '@/components/ui/ActionButtonGroup';

import { useFeatureFlag } from '@/hooks/queries/useFeatureFlagsQuery';
import { useGroupsWithBalancesQuery } from '@/hooks/queries/useGroups';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';

import { buildWebExampleGroup } from '@/lib/exampleGroup';

import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';

import { handleApiError } from '@/utils/errorUtils';

function GroupsPage() {
  const { t, ready, language } = useTranslation();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');

  // Load required namespaces
  const groupsLoaded = useNamespace('groups');
  const commonLoaded = useNamespace('common');
  const dashboardLoaded = useNamespace('dashboard');
  const membersLoaded = useNamespace('members');
  const importLoaded = useNamespace('import');
  const allNamespacesLoaded =
    groupsLoaded &&
    commonLoaded &&
    dashboardLoaded &&
    membersLoaded &&
    importLoaded;
  const { isOnline } = useNetworkStatus();
  const proEnabled = useFeatureFlag('pro');

  // The example group rides below the user's own groups until the user
  // deletes it (the API client turns that into this persisted flag); a
  // small link brings it back. Never part of `groups`; yields to an
  // active search.
  const user = useAuthStore(state => state.user);
  const exampleGroupHidden = useUIStore(state => state.exampleGroupHidden);
  const setExampleGroupHidden = useUIStore(
    state => state.setExampleGroupHidden
  );
  // Which scenario the example shows this page load (rotates per launch).
  const exampleLaunches = useUIStore(state => state.exampleGroupLaunches);
  const exampleGroup = useMemo(
    () =>
      // Not before the `groups` namespace has loaded — the fixture's names
      // come from i18n and would otherwise render as bare keys.
      exampleGroupHidden || !groupsLoaded
        ? null
        : (buildWebExampleGroup()?.group ?? null),
    // Rebuild when the user, their currency or the language changes. Keyed
    // on `language`, not `t`: the hook's `t` keeps a stable identity across
    // language switches, so a `t` dependency never recomputes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      exampleGroupHidden,
      exampleLaunches,
      groupsLoaded,
      user?.id,
      user?.name,
      user?.settings?.currency,
      language,
    ]
  );

  // Debounce search query
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch all groups with balances (no pagination)
  const {
    data: groups = [],
    error,
    isLoading,
    isError,
  } = useGroupsWithBalancesQuery(
    { search: debouncedSearchQuery || undefined },
    ready && allNamespacesLoaded
  );

  // Rendered twice on an empty list — in the header and in the empty card —
  // so each copy needs its own test id.
  const renderImportButton = (testId: string) => {
    // The header paints before the query is enabled, so this CTA has to gate on
    // its own namespace or it shows the raw key on a first visit.
    if (!importLoaded) return null;

    // Offline the import cannot even start: the file is parsed locally but the
    // job is created server-side.
    const button = (
      <Button
        variant="secondary"
        leftIcon={<Import className="w-5 h-5" />}
        className="w-full sm:w-auto"
        disabled={!isOnline}
        title={!isOnline ? t('import:errors.offline') : undefined}
        data-testid={testId}
      >
        {t('import:entry.cta')}
      </Button>
    );

    // A disabled button inside a link is still focusable and still navigates,
    // so offline the wrapper goes away with the action.
    return isOnline ? (
      <PreloadLink href="/groups/import">{button}</PreloadLink>
    ) : (
      button
    );
  };

  // Show skeleton only for initial loading
  if (isLoading && groups.length === 0) {
    return (
      <AppLayout>
        <GroupsPageSkeleton />
      </AppLayout>
    );
  }

  // Format error message
  const errorMessage = isError
    ? handleApiError(error as Error, t as TFunction, 'groups')
    : '';

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto space-y-6 sm:space-y-8">
        {/* Header with Action Buttons */}
        <div
          className="flex flex-col sm:flex sm:flex-row sm:justify-between sm:items-start gap-4"
          data-testid="groups-header"
        >
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
              {t('groups:list.title')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {t('groups:list.subtitle')}
            </p>
          </div>
          <div className="shrink-0">
            {/* Import is the secondary path, so it sits under Create on every
                width rather than beside it. */}
            <ActionButtonGroup className="sm:flex-col sm:items-end">
              <PreloadLink href="/groups/create" preloadType="create-group">
                <Button
                  variant="primary"
                  leftIcon={<PlusIcon className="w-5 h-5" />}
                  className="w-full sm:w-auto"
                  data-testid="groups-create-button"
                >
                  {t('groups:actions.create')}
                </Button>
              </PreloadLink>
              {renderImportButton('groups-import-button')}
            </ActionButtonGroup>
          </div>
        </div>

        <NativeAppCard />

        {/* Global balances across all groups */}
        <GlobalBalanceCard />

        {/* Search Bar */}
        <div className="max-w-md">
          <Input
            type="text"
            placeholder={t('groups:list.search.placeholder')}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            icon={<SearchIcon className="w-5 h-5 text-gray-400" />}
            className="w-full"
            inputClassName="text-gray-900"
            data-testid="groups-search-input"
          />
        </div>

        {/* Error State */}
        {errorMessage && (
          <Alert type="error" message={errorMessage} data-testid="alert" />
        )}

        {/* Groups Content */}
        {!errorMessage && (
          <div className="space-y-4">
            <GroupsTable
              showProPills={proEnabled}
              groups={
                exampleGroup && !debouncedSearchQuery.trim()
                  ? [...groups, exampleGroup]
                  : groups
              }
              loading={false}
              emptyMessage={
                debouncedSearchQuery.trim() && groups.length === 0
                  ? t('groups:list.search.noResults')
                  : t('groups:list.empty.title')
              }
              emptyDescription={
                debouncedSearchQuery.trim() && groups.length === 0
                  ? ''
                  : t('groups:list.empty.description')
              }
              emptyAction={
                debouncedSearchQuery.trim() && groups.length === 0
                  ? undefined
                  : renderImportButton('groups-empty-import-button')
              }
              showMemberCount={true}
              showLastActivity={true}
            />
            {exampleGroupHidden && !debouncedSearchQuery.trim() && (
              <div className="text-center">
                <button
                  type="button"
                  data-testid="example-group-show"
                  onClick={() => setExampleGroupHidden(false)}
                  className="text-sm font-medium text-orange-600 hover:underline dark:text-orange-400"
                >
                  {t('groups:exampleGroup.show')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

// Export the page wrapped with authentication
export default withAuth(GroupsPage);
